"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
// useNotificationsState.ts
var react_1 = require("react");
var api_1 = require("./../api");
var api_2 = require("../api");
var notificationsStore_1 = require("../storage/notificationsStore");
// Tabs that share the same server-side filter ('all' = non-archived).
// Switching between these two is client-side only; no re-fetch needed.
var CLIENT_SIDE_FILTERS = ['all', 'unread'];
var serverFilterFor = function (filter) {
    return filter === 'archived' ? 'archived' : 'all';
};
function useNotificationsState() {
    var _a, _b;
    var notifications = (0, react_1.useSyncExternalStore)(notificationsStore_1.subscribe, notificationsStore_1.getNotifications);
    var isLoadingMore = (0, react_1.useSyncExternalStore)(notificationsStore_1.subscribe, notificationsStore_1.getLoadingMore);
    var hasMore = (0, react_1.useSyncExternalStore)(notificationsStore_1.subscribe, notificationsStore_1.getHasMore);
    var unreadCount = (0, react_1.useMemo)(function () { return notifications.filter(function (n) { return !n.read_at && !n.archived_at; }).length; }, [notifications]);
    var _c = (0, react_1.useState)((_b = (_a = notifications[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null), selectedId = _c[0], setSelectedIdState = _c[1];
    var setSelectedId = (0, react_1.useCallback)(function (id) {
        setSelectedIdState(id);
        if (id)
            void (0, api_1.markOpened)([id]);
    }, []);
    var _d = (0, react_1.useState)('all'), filter = _d[0], setFilterState = _d[1];
    var _e = (0, react_1.useState)(''), query = _e[0], setQuery = _e[1];
    // Track which server filter produced the current notifications list.
    // Starts as 'all' because the Blade bootstrap always fetches non-archived.
    var serverFilterRef = (0, react_1.useRef)('all');
    var prevFilterRef = (0, react_1.useRef)('all');
    var isMounted = (0, react_1.useRef)(false);
    // Filter change: re-fetch from the server only when crossing the archived boundary.
    var setFilter = (0, react_1.useCallback)(function (next) {
        var prev = prevFilterRef.current;
        prevFilterRef.current = next;
        setFilterState(next);
        var crossingArchived = (next === 'archived') !== (prev === 'archived');
        if (crossingArchived) {
            var sf = serverFilterFor(next);
            serverFilterRef.current = sf;
            void (0, api_2.reloadInboxPage)(sf, query);
        }
    }, [query]);
    // Query change: debounced re-fetch from page 1.
    (0, react_1.useEffect)(function () {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        var timer = setTimeout(function () {
            void (0, api_2.reloadInboxPage)(serverFilterRef.current, query);
        }, 350);
        return function () { return clearTimeout(timer); };
    }, [query]);
    var loadMore = (0, react_1.useCallback)(function () {
        void (0, api_2.loadMoreNotifications)(serverFilterRef.current, query);
    }, [query]);
    var selectedNotification = (0, react_1.useMemo)(function () { var _a; return (_a = notifications.find(function (n) { return n.id === selectedId; })) !== null && _a !== void 0 ? _a : null; }, [notifications, selectedId]);
    var filteredNotifications = (0, react_1.useMemo)(function () {
        return notifications.filter(function (n) {
            var isRead = !!n.read_at;
            var isArchived = !!n.archived_at;
            if (filter === 'unread' && isRead)
                return false;
            if (filter === 'archived' && !isArchived)
                return false;
            if (filter !== 'archived' && isArchived)
                return false;
            if (!query.trim())
                return true;
            var q = query.toLowerCase();
            return (n.data.title.toLowerCase().includes(q) ||
                n.data.body.toLowerCase().includes(q) ||
                n.data.kind.toLowerCase().includes(q));
        });
    }, [notifications, filter, query]);
    (0, react_1.useEffect)(function () {
        var _a, _b;
        var topNotificationId = (_b = (_a = filteredNotifications[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
        if (!topNotificationId) {
            if (selectedId !== null)
                setSelectedIdState(null);
            return;
        }
        var selectedIsVisible = filteredNotifications.some(function (n) { return n.id === selectedId; });
        if (!selectedIsVisible) {
            setSelectedIdState(topNotificationId);
        }
    }, [filteredNotifications, selectedId]);
    return {
        notifications: notifications,
        filteredNotifications: filteredNotifications,
        selectedNotification: selectedNotification,
        unreadCount: unreadCount,
        filter: filter,
        query: query,
        hasMore: hasMore,
        isLoadingMore: isLoadingMore,
        setFilter: setFilter,
        setQuery: setQuery,
        setSelectedId: setSelectedId,
        loadMore: loadMore,
        markRead: api_1.markRead,
        markAllRead: function (ids) { return (0, api_1.applyNotificationAction)(ids, 'read'); },
        markUnread: api_1.markUnread,
        archive: api_1.archive,
        restore: api_1.restore,
    };
}
exports.default = useNotificationsState;
